import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  Fab,
  IconButton
} from '@mui/material';
import Header from '../components/Header';
import LottoBall from '../components/LottoBall';
import { useLotto } from '../contexts/LottoContext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import HomeIcon from '@mui/icons-material/Home';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { UserChoice } from '../types';
import axios from 'axios';

const MyNumbersScreen: React.FC = () => {
  const { 
    userChoices, 
    currentSequenceNo,
    lastSequenceNo,
    isLoading, 
    error: lottoError,
    fetchUserChoices,
    fetchPrizeBySequenceNo
  } = useLotto();
  const { isAuthenticated } = useAuth();
  const [selectedSequenceNo, setSelectedSequenceNo] = useState<number | null>(null);
  const navigate = useNavigate();

  // 로그인 체크
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/numbers' } });
    }
  }, [isAuthenticated, navigate]);

  // 초기 데이터 로드
  useEffect(() => {
    if (currentSequenceNo && !selectedSequenceNo) {
      setSelectedSequenceNo(currentSequenceNo);
    }
  }, [currentSequenceNo, selectedSequenceNo]);

  // 선택한 회차의 데이터 로드 및 사용자 선택 번호 로드를 하나의 useEffect로 통합
  useEffect(() => {
    const abortController = new AbortController();

    const loadData = async () => {
      if (!selectedSequenceNo) return;  // null 체크 추가
      
      try {
        //await fetchPrizeBySequenceNo(selectedSequenceNo, abortController.signal);
        // 첫 번째 API 호출이 성공하면 두 번째 API 호출 진행
        await fetchUserChoices(selectedSequenceNo, abortController.signal);
      } catch (error) {
        if (axios.isCancel(error)) {
          console.log('데이터 로드가 취소되었습니다.');
          return;
        }
        console.error('데이터 로드 중 오류 발생:', error);
      }
    };

    if (isAuthenticated && !abortController.signal.aborted) {
      loadData();
    }

    return () => {
      console.log('컴포넌트 언마운트: API 요청 취소');
      abortController.abort();
    };
  }, [selectedSequenceNo, isAuthenticated, fetchPrizeBySequenceNo, fetchUserChoices]);

  // 이전 회차로 이동
  const handlePreviousSequence = () => {
    if (selectedSequenceNo && selectedSequenceNo > 1) {
      setSelectedSequenceNo(selectedSequenceNo - 1);
    }
  };

  // 다음 회차로 이동
  const handleNextSequence = () => {
    if (selectedSequenceNo && lastSequenceNo && selectedSequenceNo < lastSequenceNo) {
      setSelectedSequenceNo(selectedSequenceNo + 1);
    }
  };

  // 당첨 등수에 따른 텍스트 반환
  const getRankingText = (ranking: number): string => {
    switch(ranking) {
      case 1: return '1등';
      case 2: return '2등';
      case 3: return '3등';
      case 4: return '4등';
      case 5: return '5등';
      case 6: return '낙첨';
      default: return '미확인';
    }
  };

  // 당첨 등수에 따른 색상 반환
  const getRankingColor = (ranking: number): string => {
    switch(ranking) {
      case 1: return '#FF0000';
      case 2: return '#FF6600';
      case 3: return '#FFCC00';
      case 4: return '#00CC00';
      case 5: return '#0099FF';
      case 6: return '#999999';
      default: return '#999999';
    }
  };

  if (isLoading && !selectedSequenceNo) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Header title="내 번호 확인" />
      <Container maxWidth="md" sx={{ mt: 3, pb: 8, position: 'relative' }}>
        {(lottoError) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {lottoError}
          </Alert>
        )}

        {/* 회차 선택 네비게이션 */}
        <Paper elevation={3} sx={{ p: 2, mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <IconButton 
            onClick={handlePreviousSequence} 
            disabled={!selectedSequenceNo || selectedSequenceNo <= 1 || isLoading}
            aria-label="이전 회차"
          >
            <ArrowBackIcon />
          </IconButton>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {selectedSequenceNo ? `${selectedSequenceNo}회차` : '회차 정보 로딩 중...'}
            </Typography>
            {lastSequenceNo && selectedSequenceNo && (
              <Typography variant="caption" color="text.secondary">
                {selectedSequenceNo === lastSequenceNo - 1 
                  ? '(현재 회차)' 
                  : `(최신: ${lastSequenceNo}회차)`}
              </Typography>
            )}
          </Box>
          
          <IconButton 
            onClick={handleNextSequence} 
            disabled={!selectedSequenceNo || !lastSequenceNo || selectedSequenceNo >= lastSequenceNo || isLoading}
            aria-label="다음 회차"
          >
            <ArrowForwardIcon />
          </IconButton>
        </Paper>

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!isLoading && (
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              저장된 번호
            </Typography>

            {userChoices.length > 0 ? (
              userChoices.map((choice: UserChoice, index: number) => (
                <Box
                  key={`${choice.sequenceNo}-${choice.choiceNo}`}
                  sx={{
                    mb: 3,
                    pb: 2,
                    borderBottom: index < userChoices.length - 1 ? '1px solid #eee' : 'none'
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1">
                      {choice.sequenceNo}회차 #{choice.choiceNo}
                    </Typography>
                    <Chip 
                      label={getRankingText(choice.ranking)} 
                      sx={{ 
                        bgcolor: getRankingColor(choice.ranking),
                        color: 'white',
                        fontWeight: 'bold'
                      }} 
                    />
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1, flexGrow: 1 }}>
                      <LottoBall 
                        number={choice.digit1} 
                        isHighlighted={choice.jackpot1}
                      />
                      <LottoBall 
                        number={choice.digit2} 
                        isHighlighted={choice.jackpot2}
                      />
                      <LottoBall 
                        number={choice.digit3} 
                        isHighlighted={choice.jackpot3}
                      />
                      <LottoBall 
                        number={choice.digit4} 
                        isHighlighted={choice.jackpot4}
                      />
                      <LottoBall 
                        number={choice.digit5} 
                        isHighlighted={choice.jackpot5}
                      />
                      <LottoBall 
                        number={choice.digit6} 
                        isHighlighted={choice.jackpot6}
                      />
                    </Box>
                  </Box>
                  
                  {choice.amount > 0 && (
                    <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                      당첨금: {choice.amount.toLocaleString()}원
                    </Typography>
                  )}
                  
                  {choice.remark && (
                    <Typography variant="body2" color="text.secondary">
                      {choice.remark}
                    </Typography>
                  )}
                </Box>
              ))
            ) : (
              <Typography color="text.secondary">
                {selectedSequenceNo ? `${selectedSequenceNo}회차에 저장된 번호가 없습니다.` : '저장된 번호가 없습니다.'}
              </Typography>
            )}
          </Paper>
        )}

        {/* 홈으로 돌아가는 버튼 */}
        <Fab 
          color="primary" 
          aria-label="홈으로" 
          sx={{ 
            position: 'fixed', 
            bottom: 20, 
            right: 20 
          }}
          onClick={() => navigate('/prize')}
        >
          <HomeIcon />
        </Fab>
      </Container>
    </Box>
  );
};

export default MyNumbersScreen; 